import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api/client";
import type { Facets, Page, Release, TrackRow } from "../api/types";
import {
  emptyFilters, activeCount, fromURL, toParams, toURL, toggle,
  type Filters, type Tab,
} from "../filters";
import { FilterBar } from "../components/FilterBar";
import { Card } from "../components/Card";
import { TrackList } from "../components/TrackList";
import { TracksModal } from "../components/TracksModal";
import { SpeedTestModal } from "../components/SpeedTestModal";
import { PlaylistPicker, Toast } from "../components/PlaylistPicker";
import { stop as stopPlaying } from "../components/player";

const PAGE = 60;

export function Browse() {
  const initial = fromURL(window.location.search);

  const [tab, setTab] = useState<Tab>(initial.tab);
  // Each tab keeps its own filter bar. A tag ticked on "On Bandcamp" means
  // nothing on "Not on Bandcamp", and the page has always worked this way.
  const [byTab, setByTab] = useState<Record<Tab, Filters>>({
    matched: initial.tab === "matched" ? initial.filters : emptyFilters,
    unmatched: initial.tab === "unmatched" ? initial.filters : emptyFilters,
  });
  const filters = byTab[tab];

  const [page, setPage] = useState<Page | null>(null);
  const [rows, setRows] = useState<Release[]>([]);
  const [facets, setFacets] = useState<Facets | null>(null);
  const [counts, setCounts] = useState<Record<Tab, number | null>>({
    matched: null, unmatched: null,
  });
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Two ways to read the same list. Per release is the record shop's window;
  // per track is what you want once tempo and key are on the rows, because a
  // key is a property of a track and not of the sleeve it came in.
  const [view, setView] = useState<"releases" | "tracks">("releases");
  const [trackRows, setTrackRows] = useState<TrackRow[]>([]);
  const [trackTotal, setTrackTotal] = useState(0);

  const [tracksFor, setTracksFor] = useState<Release | null>(null);
  const [speedFor, setSpeedFor] = useState<Release | null>(null);
  // Which track the Speed test should open on, when it was reached from a
  // track row rather than from a card.
  const [speedPos, setSpeedPos] = useState<number | undefined>(undefined);

  // "Add to playlist…", from a card (the whole record) or a track row (one
  // track). One picker at a time, opened where the click was.
  const [picking, setPicking] = useState<{
    what: { release: string; pos?: number; label: string }; at: { x: number; y: number };
  } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const closePicker = useCallback(() => setPicking(null), []);
  const clearToast = useCallback(() => setToast(null), []);

  // Every filter change starts a new request; only the newest one may write.
  const run = useRef(0);

  const patch = useCallback((p: Partial<Filters>) => {
    setByTab((prev) => ({ ...prev, [tab]: { ...prev[tab], ...p } }));
  }, [tab]);

  const reset = useCallback(() => {
    setByTab((prev) => ({ ...prev, [tab]: emptyFilters }));
  }, [tab]);

  // Keep the address bar in step, so a filtered view is a link and the back
  // button does what it looks like it should.
  useEffect(() => {
    const url = toURL(tab, filters);
    if (url !== window.location.pathname + window.location.search) {
      // Safari allows about a hundred of these per half minute and throws
      // past that; ticking through a tag list can get there. The address
      // bar falling a step behind is nothing; an exception here is the page.
      try { window.history.replaceState(null, "", url); } catch { /* next change */ }
    }
  }, [tab, filters]);

  // The results and the dropdowns, for the filters as they stand.
  useEffect(() => {
    const mine = ++run.current;
    setLoading(true);
    setError(null);
    stopPlaying();

    const q = toParams(tab, filters);
    const first = new URLSearchParams(q);
    first.set("limit", String(PAGE));

    // Two requests, deliberately NOT awaited together.
    //
    // They used to share a Promise.all, which meant the records waited for the
    // dropdowns: the releases arrive in about 0.8s and the facets in about
    // four, because counting 30,743 distinct tags and 13,411 labels over
    // 105,000 releases is genuinely expensive and the page asked for it before
    // showing anything. So the slower call decided when the page appeared, and
    // it is the one nobody is waiting for -- you come here to see records, and
    // notice the filter lists only when you reach for them.
    //
    // Now the records render as soon as they land and the dropdowns fill in
    // behind them. Both still honour `mine`, so a filter change that overtakes
    // an in-flight pair still cannot write stale results.
    api.releases(first)
      .then((p) => {
        if (mine !== run.current) return;
        setPage(p);
        setRows(p.releases);
        setCounts((c) => ({ ...c, [tab]: p.total }));
      })
      .catch((e: ApiError) => {
        if (mine !== run.current) return;
        setError(e.message);
        setPage(null);
        setRows([]);
      })
      .finally(() => { if (mine === run.current) setLoading(false); });

    // The dropdowns. A failure here is not shown: the page is perfectly usable
    // with the filters it already has, and an error banner over a full page of
    // records would be a worse lie than a stale tag list.
    api.facets(q)
      .then((f) => { if (mine === run.current) setFacets(f); })
      .catch(() => { /* keep whatever the dropdowns already hold */ });
  }, [tab, filters]);

  // The track view's own page. Kept separate from the release fetch rather
  // than folded into it: switching view should not refetch the releases, and
  // switching back should not have thrown them away.
  useEffect(() => {
    if (view !== "tracks") return;
    const mine = ++run.current;
    setLoading(true);
    setError(null);
    stopPlaying();
    const q = toParams(tab, filters);
    q.set("limit", String(PAGE));
    api.trackSearch(q)
      .then((p) => {
        if (mine !== run.current) return;
        setTrackRows(p.tracks);
        setTrackTotal(p.total);
      })
      .catch((e: ApiError) => {
        if (mine !== run.current) return;
        setError(e.message);
        setTrackRows([]);
      })
      .finally(() => { if (mine === run.current) setLoading(false); });
  }, [view, tab, filters]);

  // Both modals want a whole release. The track view holds tracks, so looking
  // for one in `rows` finds it only by luck -- the two lists page
  // independently, and after the first "show more" they have drifted apart.
  // Fetching by key is the honest way to get it.
  const openRelease = useCallback(
    async (key: string, put: (r: Release) => void) => {
      const known = rows.find((x) => x.key === key);
      if (known) { put(known); return; }
      try {
        put(await api.release(key));
      } catch (e) {
        setError((e as ApiError).message);
      }
    }, [rows]);

  const loadMoreTracks = useCallback(() => {
    if (trackRows.length >= trackTotal || more) return;
    const mine = run.current;
    setMore(true);
    const q = toParams(tab, filters);
    q.set("limit", String(PAGE));
    q.set("offset", String(trackRows.length));
    api.trackSearch(q)
      .then((p) => {
        if (mine !== run.current) return;
        setTrackRows((r) => [...r, ...p.tracks]);
      })
      .catch(() => { /* the page still stands; the button can be pressed again */ })
      .finally(() => { if (mine === run.current) setMore(false); });
  }, [tab, filters, trackRows.length, trackTotal, more]);

  // The other tab's total, so its label can carry a count without the reader
  // having to click it to find out.
  useEffect(() => {
    const other: Tab = tab === "matched" ? "unmatched" : "matched";
    if (counts[other] !== null) return;
    const q = toParams(other, byTab[other]);
    q.set("limit", "1");
    api.releases(q)
      .then((p) => setCounts((c) => ({ ...c, [other]: p.total })))
      .catch(() => { /* a count is a nicety; a failure here is not worth showing */ });
  }, [tab, byTab, counts]);

  const loadMore = useCallback(() => {
    if (!page || rows.length >= page.total || more) return;
    const mine = run.current;
    setMore(true);
    const q = toParams(tab, filters);
    q.set("limit", String(PAGE));
    q.set("offset", String(rows.length));
    api.releases(q)
      .then((p) => {
        if (mine !== run.current) return;
        setRows((r) => [...r, ...p.releases]);
      })
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setMore(false));
  }, [page, rows.length, more, tab, filters]);

  // Load the next page as the end of the list comes into view.
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (e) => e[0]?.isIntersecting && loadMore(),
      { rootMargin: "600px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loadMore]);

  const groups = new Map((page?.groups ?? []).map((g) => [g.id, g]));
  const done = page ? rows.length >= page.total : false;

  return (
    <>
      <nav className="tabs">
        {(["matched", "unmatched"] as Tab[]).map((t) => (
          <button key={t} className={t === tab ? "on" : ""} onClick={() => setTab(t)}>
            {t === "matched" ? "On Bandcamp" : "Not on Bandcamp"}
            {counts[t] !== null && <span className="c">{counts[t]!.toLocaleString()}</span>}
          </button>
        ))}
      </nav>

      <FilterBar
        tab={tab}
        filters={filters}
        facets={facets}
        total={page?.total ?? 0}
        loading={loading}
        onChange={patch}
        onReset={reset}
      />

      <div className="viewpick">
        <button className={view === "releases" ? "on" : ""}
                onClick={() => setView("releases")}>Per release</button>
        <button className={view === "tracks" ? "on" : ""}
                onClick={() => setView("tracks")}>Per track</button>
        {view === "tracks" && trackTotal > 0 && (
          <span className="count">{trackTotal.toLocaleString()} tracks</span>
        )}
      </div>

      {error && <div className="error">{error}</div>}

      {!error && page?.total === 0 && (
        <div className="empty">
          Nothing matches that.{" "}
          {activeCount(filters) > 0 && (
            <button className="link" onClick={reset}>Clear the filters</button>
          )}
        </div>
      )}

      {view === "releases" ? (
        <Sections
          rows={rows}
          groups={groups}
          filters={filters}
          patch={patch}
          onTracks={setTracksFor}
          onSpeed={(r) => { setSpeedPos(undefined); setSpeedFor(r); }}
          onAddToPlaylist={(r, at) => setPicking({
            what: { release: r.key, label: `${r.artist} – ${r.title}` }, at,
          })}
        />
      ) : (
        <TrackList
          rows={trackRows}
          loading={loading}
          onOpenRelease={(key) => void openRelease(key, setTracksFor)}
          onSpeedTest={(key, pos) => { setSpeedPos(pos); void openRelease(key, setSpeedFor); }}
          onAddToPlaylist={(t, at) => setPicking({
            what: { release: t.key, pos: t.pos, label: `${t.artist} – ${t.title}` }, at,
          })}
          onAddToTestList={(t) => {
            api.addToTestList(t.key, t.pos)
              .then((v) => setToast(v.added
                ? `added ${t.title} to Beat match`
                : `${t.title} is already on Beat match`))
              .catch((e: ApiError) => setToast(e.message));
          }}
        />
      )}

      {picking && (
        <PlaylistPicker
          what={picking.what}
          at={picking.at}
          onClose={closePicker}
          onDone={(msg) => { setPicking(null); setToast(msg); }}
        />
      )}
      <Toast msg={toast} onGone={clearToast} />

      <div ref={sentinel} />
      {view === "releases" && page && !done && (
        <div className="more">
          <button onClick={loadMore} disabled={more}>
            {more ? "loading…" : `Show more (${(page.total - rows.length).toLocaleString()} left)`}
          </button>
        </div>
      )}
      {view === "tracks" && trackRows.length < trackTotal && (
        <div className="more">
          <button onClick={loadMoreTracks} disabled={more}>
            {more ? "loading…" : `Show more (${(trackTotal - trackRows.length).toLocaleString()} left)`}
          </button>
        </div>
      )}
      {loading && rows.length === 0 && <div className="loading"><span className="spinner" />loading…</div>}

      {tracksFor && <TracksModal release={tracksFor} onClose={() => setTracksFor(null)} />}
      {speedFor && (
        <SpeedTestModal
          release={speedFor}
          startPos={speedPos}
          onClose={() => { setSpeedFor(null); setSpeedPos(undefined); }}
        />
      )}
    </>
  );
}

/**
 * Cards under a heading per month, newest first throughout -- one timeline
 * rather than upcoming-then-historic. The rows arrive in that order already, so
 * a heading is written whenever the group changes.
 */
function Sections({
  rows, groups, filters, patch, onTracks, onSpeed, onAddToPlaylist,
}: {
  rows: Release[];
  groups: Map<string, { kindLabel: string; monthLabel: string; count: number }>;
  filters: Filters;
  patch: (p: Partial<Filters>) => void;
  onTracks: (r: Release) => void;
  onSpeed: (r: Release) => void;
  onAddToPlaylist: (r: Release, at: { x: number; y: number }) => void;
}) {
  const out: React.ReactNode[] = [];
  let i = 0;
  while (i < rows.length) {
    const id = rows[i]!.group;
    const g = groups.get(id);
    const slice: Release[] = [];
    while (i < rows.length && rows[i]!.group === id) slice.push(rows[i++]!);

    out.push(
      <section className="month" key={id}>
        <h2 className="month">
          <span className="kind">{g?.kindLabel ?? ""}</span>
          {g?.monthLabel ?? ""}
          <span className="n">
            {(g?.count ?? slice.length).toLocaleString()} release
            {(g?.count ?? slice.length) === 1 ? "" : "s"}
          </span>
        </h2>
        <div className="grid">
          {slice.map((r) => (
            <Card
              key={r.key}
              release={r}
              activeTags={filters.tags}
              activeGenres={filters.genres}
              activeStyles={filters.styles}
              activeLabels={filters.labels}
              onTags={(t) => patch({ tags: toggle(filters.tags, t) })}
              onGenre={(t) => patch({ genres: toggle(filters.genres, t) })}
              onStyle={(t) => patch({ styles: toggle(filters.styles, t) })}
              onLabel={(t) => patch({ labels: toggle(filters.labels, t) })}
              onTracks={onTracks}
              onSpeed={onSpeed}
              onAddToPlaylist={onAddToPlaylist}
            />
          ))}
        </div>
      </section>,
    );
  }
  return <>{out}</>;
}
